// ResponsiveCDNImage.tsx
import React, { useCallback, useRef } from "react";
import { useInView } from "react-intersection-observer";

const WIDTH_BUCKETS = [160, 240, 320, 480, 640, 720, 960] as const;

function pickBucketCeil(requestedWidth: number): number {
  const ww = Math.round(requestedWidth);
  for (const b of WIDTH_BUCKETS) {
    if (b >= ww) return b;
  }
  return WIDTH_BUCKETS[WIDTH_BUCKETS.length - 1];
}

function getResponsiveWidths(requestedWidth: number): {
  w1: number;
  w2: number;
} {
  const w1 = pickBucketCeil(requestedWidth);
  const w2 = pickBucketCeil(w1 * 1.5);
  return { w1, w2 };
}

function buildCdnUrl(baseUrl: string, w: number, q: number): string {
  try {
    const url = new URL(baseUrl);
    url.searchParams.set("w", String(w));
    url.searchParams.set("q", String(q));
    return url.toString();
  } catch {
    return `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}w=${w}&q=${q}`;
  }
}

interface ResponsiveCDNImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  quality?: number;
  sizeRatio?: number;
  lazy?: boolean;
  root?: React.RefObject<Element>;
  rootMargin?: string;
  sizes?: string;
}

const ResponsiveCDNImage: React.FC<ResponsiveCDNImageProps> = ({
  src,
  alt = "",
  className = "",
  quality = 80, // 이미지 품질
  sizeRatio = 1, // 화면을 차지하는 비율, 동적 이미지 렌더링 시 사용
  lazy = false,
  root, // rootMargin을 사용하기 위해 observer's target을 지정
  rootMargin = "100px", // 이미지 로딩 시 미리 로딩할 범위
  sizes = "100vw",
  ...rest
}) => {
  const ref = useRef<HTMLImageElement | null>(null);
  const { ref: inViewRef, inView } = useInView({
    triggerOnce: true,
    rootMargin: rootMargin,
    root: root?.current,
    threshold: 0,
  });

  const setRefs = useCallback(
    (node: HTMLImageElement | null) => {
      ref.current = node;
      inViewRef(node);
    },
    [inViewRef],
  );

  const deviceWidth = Math.min(window.innerWidth, 742);
  const requested = deviceWidth * sizeRatio;
  const { w1, w2 } = getResponsiveWidths(requested);

  const baseUrl = src ?? "";
  const url1 = buildCdnUrl(baseUrl, w1, quality);
  const url2 = buildCdnUrl(baseUrl, w2, quality);
  const srcSet = `${url1} 1x, ${url2} 2x`;

  if (lazy) {
    return (
      <div ref={setRefs} className="relative w-full h-full">
        {inView ? (
          <img
            src={url1}
            srcSet={srcSet}
            sizes={sizes}
            alt={alt}
            className={className}
            {...rest}
          />
        ) : null}
      </div>
    );
  }

  return (
    <img
      ref={ref}
      src={url1}
      srcSet={srcSet}
      sizes={sizes}
      alt={alt}
      className={className}
      {...rest}
    />
  );
};

export default ResponsiveCDNImage;
